import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, AlertTriangle, LogOut } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Logout Confirmation Dialog
export const LogoutConfirmDialog = ({ isOpen, onClose, onConfirm, loading = false }) => {
  const { t } = useLanguage();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative w-full max-w-md mx-4 bg-white rounded-3xl shadow-2xl animate-slide-up">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-secondary">
              {t('profile.confirm_logout')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-orange-50 text-muted-foreground hover:text-secondary transition-colors"
              data-testid="logout-dialog-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <LogOut className="w-6 h-6 text-primary" />
            </div>
            <p className="text-muted-foreground pt-2">
              {t('profile.logout_confirm_msg')}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full py-5"
              data-testid="logout-cancel-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90 py-5"
              data-testid="logout-confirm-btn"
            >
              {loading ? t('profile.logging_out') : t('profile.log_out')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Account Deletion Confirmation Dialog
export const DeleteAccountDialog = ({ isOpen, onClose, onConfirm, loading = false }) => {
  const { t } = useLanguage();
  const [confirmText, setConfirmText] = useState("");
  const isConfirmValid = confirmText === "DELETE";
  
  if (!isOpen) return null;

  const handleClose = () => {
    setConfirmText("");
    onClose();
  };

  const handleConfirm = () => {
    if (isConfirmValid) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Dialog */}
      <div className="relative w-full max-w-md mx-4 bg-white rounded-3xl shadow-2xl animate-slide-up">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-secondary">
              {t('profile.delete_account_title')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-orange-50 text-muted-foreground hover:text-secondary transition-colors"
              data-testid="delete-dialog-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div className="space-y-2">
                <p className="text-secondary font-medium">
                  {t('profile.delete_account_warning')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('profile.delete_account_miss')}
                </p>
              </div>
            </div>
            
            {/* Warning list */}
            <div className="bg-destructive/5 rounded-2xl p-4 space-y-2">
              <p className="text-sm font-medium text-secondary">
                {t('profile.delete_will_remove')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.delete_item_profile')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.delete_item_meal_history')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.delete_item_glucose_logs')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.delete_item_insights')}
                </li>
              </ul>
              <p className="text-xs text-destructive font-medium pt-2">
                {t('profile.delete_cannot_undo')}
              </p>
            </div>
            
            {/* Safety confirmation */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary">
                {t('profile.type_delete_confirm')}
              </label>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder={t('common.type_delete')}
                className="rounded-xl border-2 border-orange-100 focus:border-destructive"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 rounded-full py-5"
              data-testid="delete-cancel-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading || !isConfirmValid}
              className="flex-1 rounded-full bg-destructive text-white hover:bg-destructive/90 py-5 disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              {loading ? t('profile.deleting') : t('profile.delete_my_account')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
